import type {
  BdVirtualClinicAdmissionCause,
} from '@bindoc-projects/virtual-clinic-domain-lib';
import {
  BD_VIRTUAL_CLINIC_ADMISSION_CAUSE_DISPLAY_NAMES,
} from '@bindoc-projects/virtual-clinic-domain-lib';
import type { FilterDetailedText } from '../filter-detailed-text';
import { BdArrayUtil } from '@bindoc-projects/util-domain-lib';
import type {
  FilterConditionPluginSpecification,
} from '../filter.plugin';
import {
  FilterConditionPluginImpl
} from '../filter.plugin';
import type { FilterConditionPlugin } from '../filter';
import type {
  BdCondition,
} from '@bindoc-projects/condition-domain-lib';
import {
  BdContentCondition,
} from '@bindoc-projects/condition-domain-lib';

export const BD_ADMISSION_CAUSE_CONDITION_ID = 'admissionCause';
export const FilterConditionAdmissionCausePluginType: FilterConditionPluginSpecification<
  BdVirtualClinicAdmissionCause[],
  FilterConditionAdmissionCauseDto,
  BdAdmissionCauseCondition
> = {
  type: 'admissionCauses',
  category: 'case',
  displayName: { de: 'Aufnahmeanlass' },
  sortingIndex: 8,
  occurrence: 'single',
  fromObject(
    obj: FilterConditionAdmissionCauseDto,
  ): FilterConditionPluginImpl<BdVirtualClinicAdmissionCause[]> {
    return new FilterConditionAdmissionCausePlugin(
        obj.admissionCauses as BdVirtualClinicAdmissionCause[],
      );
  },
  fromCondition(
    jsonObj: BdAdmissionCauseCondition,
  ): FilterConditionPluginImpl<BdVirtualClinicAdmissionCause[]> {
    return new FilterConditionAdmissionCausePlugin(jsonObj?.includes ?? []);
  },
  newInstance(): FilterConditionPluginImpl<BdVirtualClinicAdmissionCause[]> {
    return new FilterConditionAdmissionCausePlugin([]);
  },
};

export class FilterConditionAdmissionCausePlugin extends FilterConditionPluginImpl<
  BdVirtualClinicAdmissionCause[],
  FilterConditionAdmissionCauseDto
> {
  static override Specification: FilterConditionPluginSpecification<
    BdVirtualClinicAdmissionCause[],
    FilterConditionAdmissionCauseDto,
    BdAdmissionCauseCondition
  > = FilterConditionAdmissionCausePluginType;

  constructor(private admissionCauses: BdVirtualClinicAdmissionCause[]) {
    super(FilterConditionAdmissionCausePluginType.type);
  }

  clone(): FilterConditionPlugin<BdVirtualClinicAdmissionCause> {
    return new FilterConditionAdmissionCausePlugin([
      ...this.admissionCauses.slice(),
    ]);
  }

  override equals(other: FilterConditionAdmissionCausePlugin): boolean {
    return (
      super.equals(other) &&
      BdArrayUtil.equals(this.admissionCauses, other.admissionCauses)
    );
  }

  override validationToText(): string {
    if (this.admissionCauses.length === 0) {
      return 'Mindestens einen Aufnahmeanlass auswählen';
    } else {
      return '';
    }
  }

  toCondition(): BdCondition | null {
    return new BdAdmissionCauseCondition(this.admissionCauses, []);
  }

  toJSON(): FilterConditionAdmissionCauseDto | null {
    return {
      type: this.type,
      admissionCauses: this.admissionCauses,
    };
  }

  override toDetailedText(): FilterDetailedText | null {
    const title = this.toText();
    if (!title) return null;
    const items = this.admissionCauses.map((cause) => {
      const displayName =
        BD_VIRTUAL_CLINIC_ADMISSION_CAUSE_DISPLAY_NAMES[cause];
      return displayName ? `${cause} - ${displayName}` : cause;
    });
    return { title, items };
  }

  toText(): string | null {
    return this.admissionCauses.length === 1
      ? 'ein Aufnahmeanlass'
      : `${this.admissionCauses.length} Aufnahmeanlässe`;
  }

  toValue(): BdVirtualClinicAdmissionCause[] {
    return this.admissionCauses;
  }
}

class BdAdmissionCauseCondition extends BdContentCondition<BdVirtualClinicAdmissionCause> {
  constructor(
    includes: BdVirtualClinicAdmissionCause[],
    excludes: BdVirtualClinicAdmissionCause[],
  ) {
    super(BD_ADMISSION_CAUSE_CONDITION_ID, includes, excludes);
  }
}

interface FilterConditionAdmissionCauseDto {
  type: string;
  admissionCauses: string[];
}
